import { auth, firebaseConfig } from '@/firebase/config'
import * as Sentry from '@sentry/react'
import { ROLES } from '@/config/roles'

const EXPECTED_VERIFICATION_ERROR_PATTERNS = [
  'already been verified',
  'alreadyVerified',
  'Too many verification',
  'too many verification codes',
  'requested too many verification',
  'Too many failed attempts',
  'Please enter the 6-digit code',
  'Invalid or expired code',
  'Invalid code',
  'This code has expired',
  'Email verification is not required',
  'You must be signed in'
]
const EXPECTED_VERIFICATION_STATUS_CODES = new Set([400, 401, 403, 404, 423, 429])

const RATE_LIMIT_USER_MESSAGE =
  "You've requested too many verification codes. Please wait 1 hour before requesting another code."

function toUserVerificationError(error) {
  const message = error?.message || ''
  if (
    message.includes('Too many verification') ||
    message.includes('too many verification codes')
  ) {
    if (message.includes('up to 1 hour')) {
      return new Error(message)
    }
    return new Error(RATE_LIMIT_USER_MESSAGE)
  }
  return error
}

function isExpectedVerificationError(error) {
  if (EXPECTED_VERIFICATION_STATUS_CODES.has(error?.status)) return true
  const message = error?.message || ''
  return EXPECTED_VERIFICATION_ERROR_PATTERNS.some((pattern) =>
    message.includes(pattern)
  )
}

function captureVerificationError(error, operation, extra = {}) {
  if (isExpectedVerificationError(error)) return

  Sentry.captureException(error, {
    tags: {
      feature: 'auth',
      area: 'email-verification',
      operation
    },
    extra
  })
}

export function roleRequiresEmailVerification(role) {
  return role === ROLES.PARENT || role === ROLES.SCHOOL_ADMIN
}

async function callVerificationFunction(functionName, body = {}) {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('You must be signed in to verify your email')
  }

  const idToken = await currentUser.getIdToken()
  const response = await fetch(
    `${firebaseConfig.functionsUrl}/${functionName}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify(body)
    }
  )

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(
      data.error || 'Failed to complete email verification. Please try again.'
    )
    error.status = response.status
    throw error
  }

  return data
}

export async function sendVerificationOTP() {
  try {
    return await callVerificationFunction('sendEmailVerificationOTP')
  } catch (error) {
    captureVerificationError(error, 'sendVerificationOTP')
    throw toUserVerificationError(error)
  }
}

export async function verifyEmailOTP(otp) {
  const code = String(otp || '').trim()
  if (!/^\d{6}$/.test(code)) {
    throw new Error('Please enter the 6-digit code')
  }

  try {
    return await callVerificationFunction('verifyEmailOTP', { otp: code })
  } catch (error) {
    captureVerificationError(error, 'verifyEmailOTP')
    throw error
  }
}

export async function resendVerificationEmail() {
  return sendVerificationOTP()
}

export async function createAndSendVerificationEmail() {
  return sendVerificationOTP()
}
